import { ApolloError } from '@apollo/client';

/** `extensions.code` отказов, которые бэкенд (или прокси) помечает кодом. */
const PERMISSION_CODES = ['forbidden', 'permission_denied', 'unauthorized'];

/**
 * Тексты отказа без кода — ровно те, что бросает бэкенд, а не любое
 * упоминание «permission»: пятисотка вида «error while checking permission
 * cache» — сбой, и «Нет доступа» на ней отправило бы человека за правами,
 * которые у него есть.
 *
 *   - `core/permissions.py`, `users/utils.py` — «You don´t have permission»
 *     (с хвостами: «… for this materials», «… (no active role)»); апостроф
 *     бывает ´, ' и ’;
 *   - `core/permissions.py` (поле с ролями) — «The request contains a field
 *     for which you do not have permission»;
 *   - `core/permissions.py` (суперпользователь) — «… does not have access to
 *     erp».
 */
const PERMISSION_MESSAGES: readonly RegExp[] = [
  /^you don[´'’`]?t have permission\b/i,
  /^the request contains a field for which you do not have permission/i,
  /\bhave access to erp\b/i,
];

const isPermissionMessage = (message: string): boolean =>
  PERMISSION_MESSAGES.some((re) => re.test(message.trim()));

/**
 * ОТКАЗ ПО ПРАВАМ, а не любой другой сбой (500, таймаут, обрыв связи).
 *
 * Отказ и сбой обязаны выглядеть по-разному: «нет доступа» поверх сбоя
 * отправляет человека к администратору за правами, которые у него уже есть, а
 * «не удалось загрузить» с «Повторить» поверх отказа обещает, что повтор
 * поможет. И ни то ни другое не «пусто»: отказ, нарисованный как «записей
 * нет», — неправда о данных.
 *
 * Считаются: ошибка GraphQL с кодом из `PERMISSION_CODES`, ошибка GraphQL с
 * известным текстом отказа бэкенда (`PERMISSION_MESSAGES`) и сетевой ответ
 * 401/403.
 *
 * Единственная проверка на приложение: до неё было три, и две из них
 * (`/permission\s*denied/`) настоящего текста бэкенда не узнавали.
 */
export const isPermissionError = (
  error: ApolloError | null | undefined,
): boolean => {
  if (!error) return false;
  const byGraphQL = (error.graphQLErrors ?? []).some((item) => {
    const code = item?.extensions?.code;
    if (
      typeof code === 'string' &&
      PERMISSION_CODES.includes(code.toLowerCase())
    ) {
      return true;
    }
    return isPermissionMessage(item?.message || '');
  });
  if (byGraphQL) return true;
  const status = (error.networkError as { statusCode?: number } | null)
    ?.statusCode;
  return status === 401 || status === 403;
};
